import { Divider, Drawer, Grid, ListItem, Tooltip } from '@mui/material'
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material'
import * as React from 'react'
import ProfileListItem from '../../common/ProfileListItem'
import OrganizationSwitcher from '../../components/OrganizationSwitcher/OrganizationSwitcher'
import { IOrganization, IUser } from '../../Definitions'
import fullLogo from '../../Objective Surgical Logo-01.svg'
import omxLogo from '../../OmxLogoLarge.svg'
import omxLogoSmall from '../../OmxLogo.png'
import logo from '../../OBXIcon.svg'
import { INavigationSection } from '../../Routes'
import { IStores } from '../../Stores'
import NavigationSection from '../NavigationSection/NavigationSection'
import { OrganizationId } from 'src/utils/OrganizationIds'
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle'
import { inject, observer } from 'mobx-react'

const drawerClosedWidth = 72
const drawerExpandedWidth = 255

interface INavigationDrawerProps {
  currentAppOrganization?: IOrganization
  isAdmin?: boolean
  isWorkflowAdmin?: boolean
  loadOrganizationsMenu?: () => Promise<void>
  logout?: () => void
  navigationSections: INavigationSection[]
  isOpen: boolean
  isTooltipOpen: boolean
  organizationsMenu?: IOrganization[]
  switchOrganization?: (org: IOrganization) => void
  toggleDrawer: () => void
  toggleDrawerTooltip?: () => void
  user?: IUser
  isSurgicalOrganization?: boolean
}

class NavigationDrawer extends React.Component<INavigationDrawerProps> {
  public componentDidMount() {
    if (this.props.isWorkflowAdmin && this.props.loadOrganizationsMenu) {
      this.props.loadOrganizationsMenu()
    }
  }

  public componentDidUpdate(prevProps: INavigationDrawerProps) {
    if (
      !prevProps.isWorkflowAdmin &&
      this.props.isWorkflowAdmin &&
      this.props.loadOrganizationsMenu
    ) {
      this.props.loadOrganizationsMenu()
    }
  }

  public render() {
    const { logout, isOpen, isTooltipOpen } = this.props
    const expandCollapseToolTipText = isOpen
      ? 'Show Less Information'
      : 'Show More Information'

    return (
      <div>
        <Drawer
          variant="permanent"
          sx={{
            width: isOpen ? drawerExpandedWidth : drawerClosedWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: isOpen ? drawerExpandedWidth : drawerClosedWidth,
              boxSizing: 'border-box',
              transition: (theme) =>
                theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              overflowX: 'hidden',
            },
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            alignItems="stretch"
            sx={{
              height: '100%',
              overflow: 'hidden',
            }}
            wrap="nowrap"
          >
            <Grid
              item
              sx={{ overflowY: 'auto', flex: 1, minHeight: 0, maxHeight: '100%' }}
            >
              <Grid
                sx={{ height: 64 }}
                container
                direction="row"
                alignItems="center"
                item
                justifyContent="center"
              >
                {this.renderLogo()}
              </Grid>
              <Divider />
              {this.renderNavigationSections()}
            </Grid>
            <Grid
              item
              sx={{
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
                zIndex: 1,
              }}
            >
              <Divider />
              <Grid container direction="column">
                <Grid item>
                  <OrganizationSwitcher
                    expanded={isOpen}
                    currentOrganization={this.props.currentAppOrganization}
                    organizations={this.props.organizationsMenu || []}
                    switchOrganization={this.props.switchOrganization!}
                    isWorkflowAdmin={this.props.isWorkflowAdmin || false}
                  />
                </Grid>
                <Grid
                  item
                  container
                  direction={isOpen ? 'row' : 'column'}
                  wrap="nowrap"
                  alignItems="center"
                >
                  <Grid item xs={isOpen ? 7 : 12}>
                    <ProfileListItem expanded={isOpen} logout={logout!} />
                  </Grid>
                  <Grid item xs={isOpen ? 2 : 12}>
                    <ListItem
                      sx={{
                        paddingLeft: 2,
                        paddingRight: 2,
                        paddingTop: isOpen ? 1 : 0.5,
                        paddingBottom: isOpen ? 1 : 0.5,
                        justifyContent: 'center',
                        minHeight: isOpen ? 'auto' : 40,
                      }}
                    >
                      <DarkModeToggle />
                    </ListItem>
                  </Grid>
                  <Grid item xs={isOpen ? 3 : 12}>
                    <ListItem
                      sx={{
                        paddingLeft: 2,
                        paddingRight: 2,
                        paddingTop: isOpen ? 1 : 0.5,
                        paddingBottom: isOpen ? 1 : 0.5,
                        cursor: 'pointer',
                        justifyContent: 'center',
                        minHeight: isOpen ? 'auto' : 40,
                      }}
                    >
                      <Tooltip
                        title={expandCollapseToolTipText}
                        placement="right-start"
                        open={isTooltipOpen}
                      >
                        {isOpen ? (
                          <ChevronLeftOutlined
                            fontSize="large"
                            onClick={this.props.toggleDrawer}
                            onMouseOver={this.props.toggleDrawerTooltip}
                            onMouseLeave={this.props.toggleDrawerTooltip}
                          />
                        ) : (
                          <ChevronRightOutlined
                            fontSize="large"
                            onClick={this.props.toggleDrawer}
                            onMouseOver={this.props.toggleDrawerTooltip}
                            onMouseLeave={this.props.toggleDrawerTooltip}
                          />
                        )}
                      </Tooltip>
                    </ListItem>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Drawer>
      </div>
    )
  }

  private renderLogo() {
    const { isOpen } = this.props

    if (this.props.currentAppOrganization?.id === OrganizationId.Surgical) {
      return isOpen ? (
        <img src={fullLogo} alt="Objective Surgical" width="200" />
      ) : (
        <img src={logo} alt="Objective Surgical" width="50" />
      )
    } else {
      return isOpen ? (
        <img src={omxLogo} alt="Objective Medical" width="200" />
      ) : (
        <img src={omxLogoSmall} alt="Objective Medical" width="50" />
      )
    }
  }

  private renderNavigationSections(): JSX.Element[] {
    const { currentAppOrganization, navigationSections, isOpen, user } = this.props

    return navigationSections.reduce<JSX.Element[]>((sections, section, index) => {
      const sectionElement = (
        <NavigationSection
          key={'section-' + index}
          isOpen={isOpen}
          navigationSection={section}
          user={user!}
          currentOrganizationId={currentAppOrganization ? currentAppOrganization.id : ''}
        />
      )
      if (sections.length > 0) {
        sections.push(<Divider key={'divider-' + index} />)
      }
      sections.push(sectionElement)
      return sections
    }, [])
  }
}

export default inject<
  IStores,
  INavigationDrawerProps,
  Partial<INavigationDrawerProps>,
  any
>((stores: IStores) => ({
  canSwitchOrganizations: stores.global.canSwitchOrganizations,
  currentAppOrganization: stores.global.currentAppOrganization,
  isAdmin: stores.global.isAdmin,
  isWorkflowAdmin: stores.global.isWorkflowAdmin,
  isTooltipOpen: stores.global.isToggleDrawerTooltipOpen,
  loadOrganizationsMenu: () => stores.organizations.getOrganizationsMenu(),
  logout: () => stores.login.logout(),
  organizationsMenu: stores.organizations.sessionUserOrganizations,
  switchOrganization: (org: IOrganization) => stores.login.switchOrganization(org),
  toggleDrawerTooltip: () => stores.global.toggleDrawerToolTip(),
  user: stores.global.user,
}))(observer(NavigationDrawer))
